import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Card, CardContent, Button } from "@kmf/ui";
import {
  noticeApi,
  type Notice,
  type AttachedFile,
  parseDraftContent,
  getFileDownloadUrl,
} from "@/lib/api";

export function NoticeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [notice, setNotice] = useState<Notice | null>(null);
  const [files, setFiles] = useState<AttachedFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = "공지사항 - KMF 할랄위원회";
    if (id) {
      loadNotice(id);
    }
  }, [id]);

  const loadNotice = async (noticeId: string) => {
    try {
      const [noticeData, filesData] = await Promise.all([
        noticeApi.find(noticeId),
        noticeApi.files(noticeId),
      ]);

      const firstNotice = noticeData[0];
      if (firstNotice) {
        setNotice(firstNotice);
        document.title = `${firstNotice.Title} - KMF 할랄위원회`;
      } else {
        setError("공지사항을 찾을 수 없습니다.");
      }

      setFiles(filesData);
    } catch (err) {
      console.error("Failed to load notice:", err);
      setError("공지사항을 불러오는데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="public-page py-12 md:py-16">
        <Container>
          <div className="text-center text-muted-foreground">로딩 중...</div>
        </Container>
      </div>
    );
  }

  if (error || !notice) {
    return (
      <div className="public-page py-12 md:py-16">
        <Container>
          <Card className="public-panel">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">{error || "공지사항을 찾을 수 없습니다."}</p>
              <Button asChild className="mt-4">
                <Link to="/notices">목록으로 돌아가기</Link>
              </Button>
            </CardContent>
          </Card>
        </Container>
      </div>
    );
  }

  const htmlContent = parseDraftContent(notice.Content);

  return (
    <div className="public-page py-12 md:py-16">
      <Container>
        <Card className="public-panel">
          <CardContent className="p-6 md:p-8">
            {/* Header */}
            <div className="border-b pb-6">
              <span className="inline-block rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                공지사항
              </span>
              <h1 className="mt-3 text-2xl font-bold text-foreground md:text-3xl">
                {notice.Title}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                작성일: {notice.UpdateDateTime}
              </p>
            </div>

            {/* Content */}
            <div
              className="article-content prose prose-slate mt-6 max-w-none"
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />

            {/* Attached Files */}
            {files.length > 0 && (
              <div className="mt-8 border-t pt-6">
                <h3 className="mb-3 font-medium text-foreground">첨부파일</h3>
                <ul className="space-y-2">
                  {files.map((file) => (
                    <li key={file.AttachedID}>
                      <a
                        href={getFileDownloadUrl(file.AttachedID)}
                        className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                        download
                      >
                        <span aria-hidden="true">↓</span>
                        {file.FileRealName}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Back Button */}
            <div className="mt-8 border-t pt-6">
              <Button asChild variant="outline">
                <Link to="/notices">목록으로</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </Container>
    </div>
  );
}
